import { useEffect, useState } from 'react';
import { BusinessRegistry } from '@paladin-monorepo/common';
import API from '@/lib/api';

/** True when signup must pick a company from registry lookup results. */
export function isRegistryCompanyIdRequired(
  registry: BusinessRegistry,
  lookupEnabled: boolean,
): boolean {
  if (registry === BusinessRegistry.OTHER) {
    return false;
  }

  if (registry === BusinessRegistry.PL) {
    return lookupEnabled;
  }

  return true;
}

/**
 * Whether external registry lookup is configured for the selected country.
 * CZ/SK are always enabled; PL/DE depend on backend env configuration.
 */
export function useRegistryLookupEnabled(country: BusinessRegistry) {
  const [lookupEnabled, setLookupEnabled] = useState(
    () => isRegistryCompanyIdRequired(country, country !== BusinessRegistry.PL),
  );
  const [isLoading, setIsLoading] = useState(country === BusinessRegistry.PL);

  useEffect(() => {
    if (country === BusinessRegistry.OTHER) {
      setLookupEnabled(false);
      setIsLoading(false);
      return;
    }

    if (country !== BusinessRegistry.PL) {
      setLookupEnabled(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    API.get<{ lookupEnabled: boolean }>(`/registry/lookup-enabled?country=${country}`)
      .then((response) => {
        if (!cancelled) {
          setLookupEnabled(response.data.lookupEnabled);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLookupEnabled(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [country]);

  return { lookupEnabled, isLoading };
}
